import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { PageCard, PageField } from '../../components/PrivatePage';
import { AppButton } from '../../components/AppButton/AppButton';
import SelectSymbol from '../../components/SelectSymbol/SelectSymbol';
import LogButton from '../../components/Logs/LogButton';
import LogView from '../../components/Logs/LogView';
import DateTime from '../Automations/AutomationModal/ScheduleArea/DateTime';
import BotsBacktestList from './BotsBacktestList';
import ResultsTable from '../Automations/BacktestModal/ResultsTable';
import ResultsButton from '../Automations/BacktestModal/ResultsButton';
import { doBacktest } from '../../services/AutomationsService';
import { getAllBots } from '../../services/BotsService';
import { toggleBotSelection } from '../Bots/botUtils';
import { getSymbol } from '../../services/SymbolsService';
import { getMemoryIndex } from '../../services/BeholderService';
import '../Automations/Automations.css';
import './backtest.css';

const DEFAULT_BACKTEST = {
    symbol: '',
    startBase: 0,
    startQuote: 0,
    startTime: 0,
    startDate: '',
    endDate: '',
    endTime: 0,
    automationIds: [],
};

function BacktestForm() {
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState('');
    const [backtest, setBacktest] = useState(DEFAULT_BACKTEST);
    const [bots, setBots] = useState([]);
    const [showLogs, setShowLogs] = useState(false);
    const [showResults, setShowResults] = useState(false);

    function onInputChange(event) {
        setBacktest((prevState) => ({ ...prevState, [event.target.id]: event.target.value }));
    }

    function onBotsChange(event) {
        const { value: bot, checked } = event.target;
        setBacktest((prevState) => ({
            ...prevState,
            automationIds: toggleBotSelection(bot, prevState.automationIds, checked),
        }));
    }

    function onSubmit() {
        if (testing) return;

        setError('');
        setTesting(true);
        doBacktest(backtest)
            .then((result) => {
                result.automationIds = backtest.automationIds;
                setBacktest({ ...result, startTime: backtest.startTime, endTime: backtest.endTime });
                setTesting(false);
                setShowLogs(false);
                setShowResults(true);
            })
            .catch((err) => {
                console.error(err.response ? err.response.data : err);
                setError(err.response ? err.response.data : err.message);
                setTesting(false);
            });
    }

    function onLogClick() {
        if (!showLogs) setShowResults(false);
        setShowLogs(!showLogs);
    }

    function onViewResultsClick() {
        if (!showResults) setShowLogs(false);
        setShowResults(!showResults);
    }

    function onSymbolChange(event) {
        if (!event.target.value) return;

        const nextBacktest = { ...DEFAULT_BACKTEST, symbol: event.target.value };

        getSymbol(nextBacktest.symbol)
            .then((symbolObj) => getMemoryIndex(symbolObj.base, 'WALLET', null)
                .then((base) => {
                    nextBacktest.startBase = base;
                    return getMemoryIndex(symbolObj.quote, 'WALLET', null);
                })
                .then((quote) => {
                    nextBacktest.startQuote = quote;
                    setBacktest(nextBacktest);
                }))
            .catch((err) => {
                console.error(err.response ? err.response.data : err);
                setError(err.response ? err.response.data : err.message);
            });
    }

    useEffect(() => {
        if (!backtest.symbol) {
            setBots([]);
            return;
        }

        getAllBots(backtest.symbol)
            .then((results) => {
                setBots(results);
                setShowLogs(false);
                setShowResults(false);
            })
            .catch((err) => {
                console.error(err.response ? err.response.data : err);
                setError(err.response ? err.response.data : err.message);
            });
    }, [backtest.symbol]);

    const canStart = backtest.automationIds.length > 0 && !testing;

    return (
        <div className="backtest-form">
            {error ? (
                <div className="private-page-notice private-page-notice--error" role="alert">
                    <Icon icon="mdi:alert-circle-outline" aria-hidden="true" />
                    <span>{error}</span>
                </div>
            ) : null}

            <PageCard
                icon="mdi:chart-timeline-variant"
                title="Backtest setup"
                description="Choose a trading pair, select saved bots to simulate, and set the simulation window."
            >
                <PageField label="Trading pair">
                    <SelectSymbol
                        symbol={backtest.symbol}
                        onChange={onSymbolChange}
                        onlyFavorites
                        fullWidth
                    />
                </PageField>

                <PageField label="Bots">
                    {backtest.symbol ? (
                        <BotsBacktestList
                            automationIds={backtest.automationIds}
                            bots={bots}
                            onChange={onBotsChange}
                        />
                    ) : null}
                    {!backtest.symbol ? (
                        <p className="private-page-field-hint">
                            Select a trading pair to list your saved bots and automations.
                        </p>
                    ) : !bots.length ? (
                        <p className="private-page-field-hint">
                            No saved bots found for this pair. Create one from Algo, Grid, or Scheduled setup.
                        </p>
                    ) : null}
                </PageField>

                <div className="private-page-grid">
                    <PageField label="Start base" htmlFor="startBase">
                        <input
                            type="number"
                            id="startBase"
                            className="form-control"
                            placeholder="0"
                            value={backtest.startBase || ''}
                            onChange={onInputChange}
                        />
                    </PageField>
                    <PageField label="Start quote" htmlFor="startQuote">
                        <input
                            type="number"
                            id="startQuote"
                            className="form-control"
                            placeholder="0"
                            value={backtest.startQuote || ''}
                            onChange={onInputChange}
                        />
                    </PageField>
                </div>

                <PageField label="Start date">
                    <DateTime
                        id="startTime"
                        onChange={onInputChange}
                        date={backtest.startDate || ''}
                        variant="stacked"
                    />
                </PageField>

                <PageField label="End date">
                    <DateTime
                        id="endTime"
                        onChange={onInputChange}
                        date={backtest.endDate || ''}
                        variant="stacked"
                    />
                </PageField>
            </PageCard>

            {showResults && backtest.id > 0 ? (
                <PageCard
                    icon="mdi:poll"
                    title="Results"
                    description="Simulated buy and sell activity for the selected period."
                >
                    <ResultsTable data={backtest} />
                </PageCard>
            ) : null}

            {showLogs ? (
                <PageCard
                    icon="mdi:text-box-outline"
                    title="Logs"
                    description="Backtest execution log output."
                >
                    <LogView file={`backtest-${backtest.userId}`} />
                </PageCard>
            ) : null}

            <div className="private-page-actions" style={{ justifyContent: 'flex-start' }}>
                <LogButton id={backtest.id} onClick={onLogClick} />
                <ResultsButton id={backtest.id} onClick={onViewResultsClick} />
                <AppButton
                    variant="primary"
                    size="md"
                    onClick={onSubmit}
                    disabled={!canStart}
                >
                    {testing ? 'Testing…' : 'Start test'}
                </AppButton>
            </div>
        </div>
    );
}

export default BacktestForm;